/**
 * Payment Service
 * 
 * API client for payment operations.
 */

import { apiService } from './apiService';
import {
  Product,
  ProductListResponse,
  PaymentIntentCreate,
  PaymentIntentResponse,
  Payment,
  PaymentHistoryResponse,
  SubscriptionCreate,
  SubscriptionCreateResponse,
  Subscription,
  SubscriptionListResponse,
  SubscriptionCancelRequest,
  SetupIntentResponse,
  CustomerPortalResponse,
  UserEntitlements,
  ProductType,
  EcoCashQuoteRequest,
  EcoCashQuoteResponse,
  EcoCashClaimCreate,
  EcoCashClaimResponse,
  WalletSnapshot,
  PaymentCapabilitiesResponse,
  SubjectAccessPaynowInitiateRequest,
  SubjectAccessPaynowInitiateResponse,
  SubjectAccessStripeIntentRequest,
  SubjectAccessStripeIntentResponse,
  SubjectPaymentCheckoutResponse,
} from '../types/payments';

const BASE_URL = '/payments';

export const paymentService = {
  // ============= PRODUCTS =============
  
  /**
   * Get all active products
   */
  async getProducts(productType?: ProductType): Promise<ProductListResponse> {
    const params = productType ? `?product_type=${productType}` : '';
    const response = await apiService.get<ProductListResponse>(`${BASE_URL}/products${params}`);
    return response.data;
  },
  
  /**
   * Get a specific product
   */
  async getProduct(productId: string): Promise<Product> {
    const response = await apiService.get<Product>(`${BASE_URL}/products/${productId}`);
    return response.data;
  },
  
  // ============= ONE-TIME PAYMENTS =============
  
  /**
   * Create a payment intent for one-time purchase
   */
  async createPaymentIntent(data: PaymentIntentCreate): Promise<PaymentIntentResponse> {
    const response = await apiService.post<PaymentIntentResponse>(
      `${BASE_URL}/one-time/create-intent`,
      data
    );
    return response.data;
  },
  
  /**
   * Get payment history
   */
  async getPaymentHistory(limit?: number): Promise<PaymentHistoryResponse> {
    const params = limit ? `?limit=${limit}` : '';
    const response = await apiService.get<PaymentHistoryResponse>(
      `${BASE_URL}/payments/history${params}`
    );
    return response.data;
  },
  
  /**
   * Get payment details
   */
  async getPayment(paymentId: string): Promise<Payment> {
    const response = await apiService.get<Payment>(`${BASE_URL}/payments/${paymentId}`);
    return response.data;
  },
  
  // ============= SUBSCRIPTIONS =============
  
  /**
   * Create a subscription
   */
  async createSubscription(data: SubscriptionCreate): Promise<SubscriptionCreateResponse> {
    const response = await apiService.post<SubscriptionCreateResponse>(
      `${BASE_URL}/subscriptions/create`,
      data
    );
    return response.data;
  },
  
  /**
   * Get all user subscriptions
   */
  async getSubscriptions(): Promise<SubscriptionListResponse> {
    const response = await apiService.get<SubscriptionListResponse>(
      `${BASE_URL}/subscriptions`
    );
    return response.data;
  },
  
  /**
   * Get active subscription
   */
  async getActiveSubscription(): Promise<Subscription | null> {
    const response = await apiService.get<Subscription | null>(
      `${BASE_URL}/subscriptions/active`
    );
    return response.data;
  },
  
  /**
   * Cancel a subscription
   */
  async cancelSubscription(
    subscriptionId: string,
    data: SubscriptionCancelRequest
  ): Promise<Subscription> {
    const response = await apiService.post<Subscription>(
      `${BASE_URL}/subscriptions/${subscriptionId}/cancel`,
      data
    );
    return response.data;
  },
  
  // ============= SETUP INTENT =============
  
  /**
   * Create a setup intent for saving payment method
   */
  async createSetupIntent(): Promise<SetupIntentResponse> {
    const response = await apiService.post<SetupIntentResponse>(
      `${BASE_URL}/setup-intent`,
      {}
    );
    return response.data;
  },
  
  // ============= CUSTOMER PORTAL =============
  
  /**
   * Create customer portal session
   */
  async createCustomerPortal(returnUrl: string): Promise<CustomerPortalResponse> {
    const response = await apiService.post<CustomerPortalResponse>(
      `${BASE_URL}/customer-portal`,
      null,
      { params: { return_url: returnUrl } }
    );
    return response.data;
  },
  
  // ============= ENTITLEMENTS =============
  
  /**
   * Get user entitlements
   */
  async getEntitlements(): Promise<UserEntitlements> {
    const response = await apiService.get<UserEntitlements>(`${BASE_URL}/entitlements`);
    return response.data;
  },

  async getCapabilities(): Promise<PaymentCapabilitiesResponse> {
    const response = await apiService.get<PaymentCapabilitiesResponse>(`${BASE_URL}/capabilities`);
    return response.data;
  },

  async createEcoCashQuote(data: EcoCashQuoteRequest): Promise<EcoCashQuoteResponse> {
    const response = await apiService.post<EcoCashQuoteResponse>(
      `${BASE_URL}/ecocash/quote`,
      data
    );
    return response.data;
  },

  async createEcoCashClaim(data: EcoCashClaimCreate): Promise<EcoCashClaimResponse> {
    const response = await apiService.post<EcoCashClaimResponse>(
      `${BASE_URL}/ecocash/claims`,
      data
    );
    return response.data;
  },

  async getEcoCashClaim(claimId: string): Promise<EcoCashClaimResponse> {
    const response = await apiService.get<EcoCashClaimResponse>(
      `${BASE_URL}/ecocash/claims/${claimId}`
    );
    return response.data;
  },

  async getWalletSnapshot(): Promise<WalletSnapshot> {
    const response = await apiService.get<WalletSnapshot>(`${BASE_URL}/wallet`);
    return response.data;
  },

  async createSubjectAccessStripeIntent(
    data: SubjectAccessStripeIntentRequest
  ): Promise<SubjectAccessStripeIntentResponse> {
    const response = await apiService.post<SubjectAccessStripeIntentResponse>(
      `${BASE_URL}/subject-access/stripe-intent`,
      data
    );
    return response.data;
  },

  async createSubjectAccessPaynowInitiate(
    data: SubjectAccessPaynowInitiateRequest
  ): Promise<SubjectAccessPaynowInitiateResponse> {
    const response = await apiService.post<SubjectAccessPaynowInitiateResponse>(
      `${BASE_URL}/subject-access/paynow-initiate`,
      data
    );
    return response.data;
  },

  async getSubjectAccessCheckout(
    checkoutReference: string
  ): Promise<SubjectPaymentCheckoutResponse> {
    const response = await apiService.get<SubjectPaymentCheckoutResponse>(
      `${BASE_URL}/subject-access/checkouts/${checkoutReference}`
    );
    return response.data;
  },
};
